import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { getApiErrorMessage } from '../lib/apiErrors';
import type { StatusLookupResponse, PriorityLookupResponse, ArticleStatusLookupResponse, LookupsResponse } from '../types/tickets';

export const LOOKUPS_QUERY_KEY = 'lookups';
export const STATUSES_QUERY_KEY = 'statuses';
export const PRIORITIES_QUERY_KEY = 'priorities';
export const ARTICLE_STATUSES_QUERY_KEY = 'article-statuses';

export function useStatuses() {
  return useQuery({
    queryKey: [STATUSES_QUERY_KEY],
    queryFn: async (): Promise<StatusLookupResponse[]> => {
      const response = await apiService.getStatuses();
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch statuses'));
      }
      return Array.isArray(response.data) ? response.data : [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - lookups rarely change
  });
}

export function usePriorities() {
  return useQuery({
    queryKey: [PRIORITIES_QUERY_KEY],
    queryFn: async (): Promise<PriorityLookupResponse[]> => {
      const response = await apiService.getPriorities();
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch priorities'));
      }
      return Array.isArray(response.data) ? response.data : [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - lookups rarely change
  });
}

export function useArticleStatuses() {
  return useQuery({
    queryKey: [ARTICLE_STATUSES_QUERY_KEY],
    queryFn: async (): Promise<ArticleStatusLookupResponse[]> => {
      const response = await apiService.getArticleStatuses();
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch article statuses'));
      }
      return Array.isArray(response.data) ? response.data : [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - lookups rarely change
  });
}

export function useAllLookups() {
  return useQuery({
    queryKey: [LOOKUPS_QUERY_KEY],
    queryFn: async (): Promise<LookupsResponse> => {
      const response = await apiService.getAllLookups();
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch lookups'));
      }
      return response.data || { statuses: [], priorities: [], articleStatuses: [] };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - lookups rarely change
  });
}